import "./services/httpAuthConfig";
import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster } from "sonner";
import { 
  MessageCircle, LayoutDashboard, Settings, Package, Users, 
  FileText, Calculator, Boxes, Plug, HelpCircle, 
  Lock, ChevronLeft, ChevronRight, DollarSign, Sun, Moon, ShoppingCart, Bot,
  LogOut, Map, Sparkles, TrendingUp, Image, GraduationCap, Video, Eye,
  Truck, BookOpen, BarChart3, Globe, Zap, Brain, Terminal, Rocket, CreditCard, Tag, Award, Banknote, Wallet, UserPlus, ScrollText, Store, Undo2, Trophy
} from "lucide-react";

// Import components
import Dashboard from "@/components/sections/Dashboard";
import AdminDashboard from "@/components/sections/AdminDashboard";
import Chat from "@/components/sections/Chat";
import IaGenerator from "@/components/sections/IaGenerator";
import Pricing from "@/components/sections/Pricing";
import Integrations from "@/components/sections/Integrations";
import Fornecedor from "@/components/sections/Fornecedor";
import Afiliado from "@/components/sections/Afiliado";
import AfiliadoUSA from "@/components/sections/AfiliadoUSA";
import SocialCommerce from "@/components/sections/SocialCommerce";
import FornecedorInternacional from "@/components/sections/FornecedorInternacional";
import AdminGlobalDashboard from "@/components/sections/AdminGlobalDashboard";
import DeveloperPortal from "@/components/sections/DeveloperPortal";
import IAGlobalDashboard from "@/components/sections/IAGlobalDashboard";
import GestaoPlanos from "@/components/sections/GestaoPlanos";
import RepassesUSD from "@/components/sections/RepassesUSD";
import CampanhasCupons from "@/components/sections/CampanhasCupons";
import RankingFornecedores from "@/components/sections/RankingFornecedores";
import DepositosPixAdmin from "@/components/sections/DepositosPixAdmin";
import CriarColaborador from "@/components/sections/CriarColaborador";
import AuditoriaCentral from "@/components/sections/AuditoriaCentral";
import MeusMarketplaces from "@/components/sections/MeusMarketplaces";
import AutomacaoAfiliados from "@/components/sections/AutomacaoAfiliados";
import LiberarIniciantes from "@/components/sections/LiberarIniciantes";
import EstornosAdmin from "@/components/sections/EstornosAdmin";
import RankingAfiliados from "@/components/sections/RankingAfiliados";
import PainelInteligencia from "@/components/sections/PainelInteligencia";
import JanelaFlutuanteLive from "@/components/JanelaFlutuanteLive";
import RoboFlutuante from "@/components/RoboFlutuante";
import { IdiomaProvider } from "./i18n/IdiomaContext";
import FinanceiroReal from "@/components/sections/FinanceiroReal";
import Control from "@/components/sections/Control";
import Catalog from "@/components/sections/Catalog";
import TrendsDashboard from "@/components/sections/TrendsDashboard";
import VideoManager from "@/components/sections/VideoManager";
import AfiliadoTutorials from "@/components/sections/AfiliadoTutorials";
import FornecedorTutorials from "@/components/sections/FornecedorTutorials";
import IAImageAds from "@/components/sections/IAImageAds";
import Placeholder from "@/components/sections/Placeholder";
import Orders from "@/components/sections/Orders";
import PainelRobo from "@/components/sections/PainelRobo";
import AuthPage from "@/components/sections/AuthPage";
import AvisoCertificadoObrigatorio from "@/components/AvisoCertificadoObrigatorio";
import ArchitectureDiagram from "@/components/sections/ArchitectureDiagram";
import AdminOverview from "@/components/sections/AdminOverview";
import MarketplaceAnalytics from "@/components/sections/MarketplaceAnalytics";
import Contabilidade from "@/components/sections/Contabilidade";
import MeusProdutos from "@/components/sections/MeusProdutos";
import { authService } from "./services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Estado de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Estado da navegação
  const [currentSection, setCurrentSection] = useState("dashboard");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  // Estado do chat
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Verificar autenticação ao carregar
  useEffect(() => {
    const checkAuth = () => {
      const user = authService.getUser();
      const token = authService.getToken();
      
      if (user && token) {
        setCurrentUser(user);
        setIsAuthenticated(true);
      }
      setAuthLoading(false);
    };
    
    checkAuth();
  }, []);
  
  // Papeis que sao administrativos - ACHADO DA SESSAO DE DEPLOY: antes só
  // reconhecia o texto exato "admin", mas o sistema usa papeis mais
  // específicos (super_admin, admin_financeiro, admin_operacional) -
  // ninguém com esses papeis caía na tela certa, iam sempre parar na
  // tela de Afiliado por eliminação.
  const ADMIN_ROLES = ['admin', 'super_admin', 'admin_financeiro', 'admin_operacional'];

  // Função de login
  const handleLogin = (user) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    // Redirecionar para seção padrão baseada no role
    if (ADMIN_ROLES.includes(user.role)) {
      setCurrentSection("dashboard");
    } else if (user.role === 'fornecedor') {
      setCurrentSection("fornecedor");
    } else if (user.role === 'fornecedor_internacional') {
      setCurrentSection("fornecedor-internacional");
    } else {
      setCurrentSection("afiliado");
    }
  };
  
  // Função de logout
  const handleLogout = async () => {
    await authService.logout();
    setCurrentUser(null);
    setIsAuthenticated(false);
    setCurrentSection("dashboard");
  };

  // Estado da IA de Anúncios
  const [iaForm, setIaForm] = useState({
    nome_produto: "",
    categoria: "",
    plataforma: "mercado_livre",
    publico_alvo: "",
    beneficios: ""
  });
  const [iaSteps, setIaSteps] = useState([
    { id: "titulo", label: "Gerando título otimizado", status: "pending" },
    { id: "imagens", label: "Criando imagens profissionais", status: "pending" },
    { id: "descricao", label: "Escrevendo descrição persuasiva", status: "pending" },
    { id: "video", label: "Produzindo vídeo automático", status: "pending" },
    { id: "cadastro", label: "Preparando cadastro nos marketplaces", status: "pending" }
  ]);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaResult, setIaResult] = useState(null);
  const [iaError, setIaError] = useState(null);

  // Seções da aplicação organizadas por painel
  const sections = [
    // ===== PAINEL CENTRAL (ADMIN) =====
    { id: "dashboard", name: "Dashboard Admin", icon: LayoutDashboard, panel: "admin", group: "ADMIN" },
    { id: "painel-robo", name: "Painel do Robo", icon: Bot, panel: "admin" },
    { id: "control", name: "Painel de Controle", icon: Settings, panel: "admin" },
    { id: "admin-overview", name: "Visao Geral (Editar)", icon: Eye, panel: "admin" },
    { id: "video-manager", name: "Gerenciar Videos", icon: Video, panel: "admin" },
    { id: "orders", name: "Todos os Pedidos", icon: ShoppingCart, panel: "admin" },
    { id: "admin-global", name: "Dashboard Global (BR+Internacional)", icon: Rocket, panel: "admin" },
    { id: "developer-portal", name: "Developer Portal (API)", icon: Terminal, panel: "admin" },
    { id: "ia-global", name: "IA Global", icon: Brain, panel: "admin" },
    { id: "gestao-planos", name: "Gestao de Planos", icon: CreditCard, panel: "admin" },
    { id: "repasses-usd", name: "Repasses Internacionais (USD)", icon: Globe, panel: "admin" },
    { id: "ranking-fornecedores", name: "Desempenho dos Fornecedores", icon: Award, panel: "admin" },
    { id: "depositos-pix", name: "Depositos PIX Pendentes", icon: Wallet, panel: "admin" },
    { id: "criar-colaborador", name: "Criar Administrador", icon: UserPlus, panel: "admin" },
    { id: "automacao-afiliados", name: "Modo Automático 24/7", icon: Zap, panel: "admin" },
    { id: "liberar-iniciantes", name: "Liberar Plano Iniciante", icon: Users, panel: "admin" },
    { id: "estornos-admin", name: "Solicitações de Estorno", icon: Undo2, panel: "admin" },
    { id: "auditoria-central", name: "Auditoria Central", icon: ScrollText, panel: "admin" },
    { id: "financeiro-real", name: "Financeiro Real (PIX)", icon: Banknote, panel: "admin" },
    { id: "architecture", name: "Arquitetura do Sistema", icon: Map, panel: "admin" },
    
    // ===== PAINEL DO AFILIADO =====
    { id: "afiliado", name: "Meu Painel (Afiliado)", icon: Users, panel: "afiliado", group: "AFILIADO" },
    { id: "afiliado-tutorials", name: "Central de Aprendizado", icon: GraduationCap, panel: "afiliado" },
    { id: "ia-image-ads", name: "IA por Imagem", icon: Image, panel: "afiliado" },
    { id: "trends", name: "Tendencias Tempo Real", icon: TrendingUp, panel: "afiliado" },
    { id: "catalog", name: "Catalogo de Produtos", icon: Package, panel: "afiliado" },
    { id: "afiliado-usa", name: "Painel Internacional", icon: Globe, panel: "afiliado" },
    { id: "meus-marketplaces", name: "Onde Vender", icon: Store, panel: "afiliado" },
    { id: "ranking-afiliados", name: "Ranking de Afiliados", icon: Trophy, panel: "afiliado" },
    { id: "painel-inteligencia", name: "Painel de Inteligência", icon: Sparkles, panel: "all" },
    { id: "social-commerce", name: "Social Commerce", icon: Zap, panel: "afiliado" },
    { id: "fornecedor-internacional", name: "Fornecedor Internacional", icon: Globe, panel: "fornecedor-internacional" },
    { id: "integrations", name: "Minhas Integracoes", icon: Plug, panel: "afiliado" },
    { id: "marketplace-analytics", name: "Analytics Marketplaces", icon: BarChart3, panel: "afiliado" },
    { id: "ia-generator", name: "IA de Anuncios", icon: Sparkles, panel: "afiliado" },
    { id: "contabilidade-afiliado", name: "Contabilidade", icon: Calculator, panel: "afiliado" },
    { id: "pricing", name: "Planos e Precos", icon: DollarSign, panel: "afiliado" },
    
    // ===== PAINEL DO FORNECEDOR =====
    { id: "fornecedor", name: "Meu Painel (Fornecedor)", icon: Truck, panel: "fornecedor", group: "FORNECEDOR" },
    { id: "fornecedor-tutorials", name: "Tutoriais", icon: BookOpen, panel: "fornecedor" },
    { id: "fornecedor-products", name: "Meus Produtos", icon: Package, panel: "fornecedor" },
    { id: "fornecedor-orders", name: "Pedidos", icon: FileText, panel: "fornecedor" },
    { id: "campanhas-cupons", name: "Campanhas e Cupons", icon: Tag, panel: "fornecedor" },
    { id: "contabilidade-fornecedor", name: "Contabilidade", icon: Calculator, panel: "fornecedor" },
    
    // ===== COMUM A TODOS =====
    { id: "support", name: "Suporte (Chat IA)", icon: MessageCircle, panel: "all" },
    { id: "help", name: "Ajuda", icon: HelpCircle, panel: "all" },
  ];


  // Filtrar seções baseado no role do usuário
  const getVisibleSections = () => {
    if (!currentUser) return [];
    const role = currentUser.role;
    
    return sections.filter(section => {
      if (section.panel === 'all') return true;
      if (ADMIN_ROLES.includes(role)) return true;
      if (role === 'fornecedor') return section.panel === 'fornecedor' || section.panel === 'all';
      if (role === 'fornecedor_internacional') return section.panel === 'fornecedor-internacional' || section.panel === 'all';
      if (role === 'afiliado') return section.panel === 'afiliado' || section.panel === 'all';
      return false;
    });
  };

  const visibleSections = getVisibleSections();


  useEffect(() => {
    if (currentSection === "support") {
      loadConversations();
    }
  }, [currentSection]);

  useEffect(() => {
    if (currentConversation) {
      loadMessages(currentConversation.id);
    }
  }, [currentConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Aplicar tema
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error("Erro ao carregar conversas:", error);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
    }
  };

  const createNewConversation = async () => {
    try {
      const response = await axios.post(`${API}/conversations`, {
        title: "Nova Conversa"
      });
      setConversations([response.data, ...conversations]);
      setCurrentConversation(response.data);
      setMessages([]);
    } catch (error) {
      console.error("Erro ao criar conversa:", error);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await axios.delete(`${API}/conversations/${conversationId}`);
      setConversations(conversations.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Erro ao deletar conversa:", error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const messageText = inputMessage;
    setInputMessage("");
    setIsLoading(true);

    try {
      const token = authService.getToken();
      const response = await axios.post(`${API}/chat`, {
        message: messageText,
        conversation_id: currentConversation?.id,
        contexto_painel: currentSection
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages([...messages, response.data.user_message, response.data.assistant_message]);

      if (!currentConversation) {
        const newConv = await axios.get(`${API}/conversations/${response.data.conversation_id}`);
        setCurrentConversation(newConv.data);
        setConversations([newConv.data, ...conversations]);
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      alert("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  // IA Generator handlers
  const updateIaStepStatus = (id, status) => {
    setIaSteps((prev) =>
      prev.map((step) => (step.id === id ? { ...step, status } : step))
    );
  };

  const handleIaSubmit = async (e) => {
    e.preventDefault();
    setIaLoading(true);
    setIaError(null);
    setIaResult(null);
    
    // Reset steps
    setIaSteps([
      { id: "titulo", label: "Gerando título otimizado", status: "pending" },
      { id: "imagens", label: "Criando imagens profissionais", status: "pending" },
      { id: "descricao", label: "Escrevendo descrição persuasiva", status: "pending" },
      { id: "video", label: "Produzindo vídeo automático", status: "pending" },
      { id: "cadastro", label: "Preparando cadastro nos marketplaces", status: "pending" }
    ]);

    try {
      const beneficiosArray = iaForm.beneficios
        .split("\n")
        .map((b) => b.trim())
        .filter(Boolean);

      // Simular progresso visual
      updateIaStepStatus("titulo", "active");
      await new Promise(res => setTimeout(res, 800));

      // Chamar API
      const response = await axios.post(`${API}/ia/generate-ad`, {
        nome_produto: iaForm.nome_produto,
        categoria: iaForm.categoria,
        plataforma: iaForm.plataforma,
        publico_alvo: iaForm.publico_alvo,
        beneficios: beneficiosArray
      });

      updateIaStepStatus("titulo", "done");
      updateIaStepStatus("imagens", "active");
      await new Promise(res => setTimeout(res, 600));

      updateIaStepStatus("imagens", "done");
      updateIaStepStatus("descricao", "active");
      await new Promise(res => setTimeout(res, 600));

      updateIaStepStatus("descricao", "done");
      updateIaStepStatus("video", "active");
      await new Promise(res => setTimeout(res, 500));

      updateIaStepStatus("video", "done");
      updateIaStepStatus("cadastro", "active");
      await new Promise(res => setTimeout(res, 500));

      updateIaStepStatus("cadastro", "done");

      setIaResult(response.data);
    } catch (err) {
      console.error("Erro ao gerar anúncio:", err);
      setIaError(err.response?.data?.detail || "Erro ao gerar anúncio");
    } finally {
      setIaLoading(false);
    }
  };

  // Renderizar conteúdo baseado na seção
  const renderContent = () => {
    const section = sections.find(s => s.id === currentSection);

    if (currentSection === "dashboard") {
      const roleAtual = authService.getUser()?.role;
      if (!ADMIN_ROLES.includes(roleAtual)) {
        return <div className="p-8 text-center text-red-400">Acesso restrito ao administrador.</div>;
      }
      return <AdminDashboard setCurrentSection={setCurrentSection} />;
    } else if (currentSection === "painel-robo") {
      return <PainelRobo />;
    } else if (currentSection === "support") {
      return (
        <Chat
          conversations={conversations}
          currentConversation={currentConversation}
          messages={messages}
          inputMessage={inputMessage}
          isLoading={isLoading}
          messagesEndRef={messagesEndRef}
          setCurrentConversation={setCurrentConversation}
          setInputMessage={setInputMessage}
          createNewConversation={createNewConversation}
          deleteConversation={deleteConversation}
          sendMessage={sendMessage}
        />
      );
    } else if (currentSection === "ia-generator") {
      return (
        <IaGenerator
          iaForm={iaForm}
          setIaForm={setIaForm}
          iaSteps={iaSteps}
          iaLoading={iaLoading}
          iaResult={iaResult}
          iaError={iaError}
          handleIaSubmit={handleIaSubmit}
          setCurrentSection={setCurrentSection}
        />
      );
    } else if (currentSection === "pricing") {
      return <Pricing />;
    } else if (currentSection === "integrations") {
      return <Integrations setCurrentSection={setCurrentSection} userRole={currentUser?.role || 'afiliado'} />;
    } else if (currentSection === "fornecedor") {
      return <Fornecedor />;
    } else if (currentSection === "afiliado") {
      return <Afiliado setCurrentSection={setCurrentSection} />;
    } else if (currentSection === "afiliado-usa") {
      return <AfiliadoUSA />;
    } else if (currentSection === "meus-marketplaces") {
      return <MeusMarketplaces usuarioId={authService.getUser()?.id} />;
    } else if (currentSection === "ranking-afiliados") {
      return <RankingAfiliados usuarioId={authService.getUser()?.id} />;
    } else if (currentSection === "painel-inteligencia") {
      return <PainelInteligencia usuarioId={authService.getUser()?.id} />;
    } else if (currentSection === "social-commerce") {
      return <SocialCommerce />;
    } else if (currentSection === "fornecedor-internacional") {
      return <FornecedorInternacional />;
    } else if (currentSection === "admin-global") {
      return <AdminGlobalDashboard />;
    } else if (currentSection === "developer-portal") {
      return <DeveloperPortal />;
    } else if (currentSection === "ia-global") {
      return <IAGlobalDashboard />;
    } else if (currentSection === "gestao-planos") {
      return <GestaoPlanos />;
    } else if (currentSection === "repasses-usd") {
      return <RepassesUSD />;
    } else if (currentSection === "campanhas-cupons") {
      return <CampanhasCupons />;
    } else if (currentSection === "ranking-fornecedores") {
      return <RankingFornecedores />;
    } else if (currentSection === "depositos-pix") {
      return <DepositosPixAdmin />;
    } else if (currentSection === "criar-colaborador") {
      return <CriarColaborador />;
    } else if (currentSection === "automacao-afiliados") {
      return <AutomacaoAfiliados />;
    } else if (currentSection === "liberar-iniciantes") {
      return <LiberarIniciantes />;
    } else if (currentSection === "estornos-admin") {
      return <EstornosAdmin />;
    } else if (currentSection === "auditoria-central") {
      return <AuditoriaCentral />;
    } else if (currentSection === "financeiro-real") {
      return <FinanceiroReal />;
    } else if (currentSection === "control") {
      return <Control setCurrentSection={setCurrentSection} />;
    } else if (currentSection === "catalog") {
      return <Catalog />;
    } else if (currentSection === "trends") {
      return <TrendsDashboard />;
    } else if (currentSection === "video-manager") {
      return <VideoManager />;
    } else if (currentSection === "afiliado-tutorials") {
      return <AfiliadoTutorials />;
    } else if (currentSection === "fornecedor-tutorials") {
      return <FornecedorTutorials />;
    } else if (currentSection === "ia-image-ads") {
      return <IAImageAds />;
    } else if (currentSection === "orders" || currentSection === "fornecedor-orders") {
      return <Orders />;
    } else if (currentSection === "architecture") {
      return <ArchitectureDiagram />;
    } else if (currentSection === "admin-overview") {
      return <AdminOverview />;
    } else if (currentSection === "marketplace-analytics") {
      return <MarketplaceAnalytics />;
    } else if (currentSection === "contabilidade-afiliado") {
      return <Contabilidade userRole="afiliado" />;
    } else if (currentSection === "contabilidade-fornecedor") {
      return <Contabilidade userRole="fornecedor" />;
    } else if (currentSection === "fornecedor-products") {
      return <MeusProdutos />;
    } else {
      return <Placeholder section={section} />;
    }
  };

  // Classes de tema
  const themeClasses = isDarkMode 
    ? "bg-gray-950 text-white" 
    : "bg-gray-100 text-gray-900";
  
  const sidebarClasses = isDarkMode
    ? "bg-gray-900 border-gray-800"
    : "bg-white border-gray-200";
  
  const sidebarTextClasses = isDarkMode
    ? "text-gray-400 hover:text-white hover:bg-gray-800"
    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100";

  // Se está carregando autenticação, mostrar loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center overflow-y-auto">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  // Pagina publica de completar cadastro apos pagamento aprovado na
  // Hotmart. Acessivel mesmo sem login, pois o afiliado ainda nao tem
  // conta - o token na URL e o que garante a seguranca.
  if (window.location.pathname === "/completar-cadastro") {
    const CompletarCadastro = require("./components/sections/CompletarCadastro").default;
    return (
      <IdiomaProvider>
        <>
          <Toaster position="top-right" richColors closeButton theme="dark" />
          <CompletarCadastro />
        </>
      </IdiomaProvider>
    );
  }

  // Rota secreta de login administrativo - separada do login
  // (fornecedor/afiliado). Só quem tem esse link exato encontra esta
  // tela; não existe nenhum link visível levando até aqui.
  const ehRotaAdminSecreta = window.location.pathname === "/painel-interno-clickhub";

  // Landing page publica na raiz do site, para visitantes nao logados
  // que nao estejam usando um link direto de login (dono) nem de
  // cadastro pos-pagamento.
  if (!isAuthenticated && window.location.pathname === "/" && !ehRotaAdminSecreta) {
    const LandingPage = require("./components/sections/LandingPage").default;
    return (
      <IdiomaProvider>
        <>
          <Toaster position="top-right" richColors closeButton theme="dark" />
          <LandingPage />
        </>
      </IdiomaProvider>
    );
  }

  // Se não está autenticado, mostrar página de login
  if (!isAuthenticated) {
    return (
      <IdiomaProvider>
      <>
        <Toaster position="top-right" richColors closeButton theme="dark" />
        <AuthPage onLogin={handleLogin} modoAdmin={ehRotaAdminSecreta} />
      </>
      </IdiomaProvider>
    );
  }

  // Certificado digital obrigatorio - so exigido de fornecedor/afiliado
  // (Brasil e Internacional), nao de admin (que tem seu proprio sistema
  // de certificado, usado como metodo de login).
  const usuarioAtualParaCertificado = authService.getUser();
  const precisaCertificadoObrigatorio = false; // TEMPORARIAMENTE DESATIVADO - reativar trocando para: ['fornecedor', 'afiliado', 'afiliado_usa'].includes(usuarioAtualParaCertificado?.role)

  return (
    <IdiomaProvider>
    <AvisoCertificadoObrigatorio
      usuarioId={precisaCertificadoObrigatorio ? usuarioAtualParaCertificado?.id : null}
    >
    <div className={`flex h-screen ${themeClasses}`} data-testid="clickhub-app">
      {/* Sonner Toaster for notifications */}
      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        theme={isDarkMode ? "dark" : "light"}
      />
      
      {/* Sidebar */}
      <div 
        className={`${
          isSidebarCollapsed ? "w-20" : "w-64"
        } ${sidebarClasses} border-r transition-all duration-300 flex flex-col`}
        data-testid="sidebar"
      >
        <div className={`p-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'} flex items-center justify-between`}>
          {!isSidebarCollapsed && (
            <div className="flex items-center gap-2.5">
              <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-8 h-8 rounded-lg object-contain" />
              <div>
                <h1 className={`text-lg font-bold leading-none ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>ClickHub</h1>
                <p className="text-[10px] text-gray-500 leading-none mt-0.5">Sistema SaaS</p>
              </div>
            </div>
          )}
          {isSidebarCollapsed && (
            <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-8 h-8 rounded-lg object-contain mx-auto" />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="ml-auto"
            data-testid="toggle-sidebar-btn"
          >
            {isSidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>
        </div>

        <ScrollArea className="flex-1 p-2">
          <div className="space-y-0.5">
            {visibleSections.map((section) => {
              const Icon = section.icon;
              const isActive = currentSection === section.id;
              
              return (
                <React.Fragment key={section.id}>
                  {section.group && !isSidebarCollapsed && (
                    <div className="pt-4 pb-1 px-3 first:pt-1">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                        {section.group}
                      </p>
                    </div>
                  )}
                  {section.group && isSidebarCollapsed && (
                    <div className="my-2 mx-3 border-t border-gray-800" />
                  )}
                  <Button
                    variant="ghost"
                    className={`w-full justify-start gap-3 h-9 ${
                      isActive
                        ? "bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 font-medium"
                        : isDarkMode 
                          ? "text-gray-400 hover:text-white hover:bg-gray-800/60" 
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                    onClick={() => setCurrentSection(section.id)}
                    data-testid={`nav-${section.id}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-orange-400' : ''}`} />
                    {!isSidebarCollapsed && (
                      <span className="text-sm truncate">{section.name}</span>
                    )}
                  </Button>
                </React.Fragment>
              );
            })}
          </div>
        </ScrollArea>

        {/* User Info + Logout */}
        <div className={`p-3 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
          {!isSidebarCollapsed && currentUser && (
            <div className="mb-2 px-2">
              <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {currentUser.nome}
              </p>
              <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-400 capitalize">
                {currentUser.role}
              </span>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`flex-1 ${isDarkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-100'}`}
              data-testid="toggle-theme-btn"
            >
              {isDarkMode ? (
                <>
                  <Sun className="w-4 h-4" />
                  {!isSidebarCollapsed && <span className="ml-2">Claro</span>}
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4" />
                  {!isSidebarCollapsed && <span className="ml-2">Escuro</span>}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-red-800 text-red-400 hover:bg-red-900/30"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {!isSidebarCollapsed && <span className="ml-2">Sair</span>}
            </Button>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal - CORRIGIDO (achado da revisao de organizacao):
          antes tinha overflow-hidden aqui, o que cortava/escondia
          qualquer conteudo maior que a altura da janela, em qualquer
          tela que nao tivesse seu proprio ScrollArea interno (a maioria
          das telas novas nao tinha). Agora permite rolar. */}
      <div className={`flex-1 flex flex-col overflow-y-auto ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
        {renderContent()}
      </div>

      <JanelaFlutuanteLive />
      <RoboFlutuante currentSection={currentSection} setCurrentSection={setCurrentSection} />
    </div>
    </AvisoCertificadoObrigatorio>
    </IdiomaProvider>
  );
}

export default App;
